import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit, Renderer2, ViewChild } from '@angular/core';
import { Galleria } from 'primeng/galleria';
import { fromEvent, Subscription } from 'rxjs';
import { GalleriaData, ResponsiveOptions } from './image-viewer.model';
import { ImageViewerService } from './image-viewer.service';
import { NgxImageZoomComponent } from "ngx-image-zoom";

@Component({
	selector: 'lb-image-viewer',
	templateUrl: './image-viewer.component.html',
	styleUrls: ['./image-viewer.component.css']
})
export class ImageViewerComponent implements OnInit {
	@ViewChild('imageZoomComponent')
	set imageZoomComponent(val) {
		this._imageZoomComponent = val;

		if (val) {
			this.overrideZoomFunction(val);
		}
	};

	get imageZoomComponent() {
		return this._imageZoomComponent;
	}

	private _imageZoomComponent?: NgxImageZoomComponent;

	get zoomingEnabled() {
		return !!(this.imageZoomComponent as any)?.zoomingEnabled ?? false;
	}

	images!: GalleriaData[];
	reinitializingNgxImageZoom = false;
	responsiveOptions: ResponsiveOptions[] = [
		{
            breakpoint: '1500px',
            numVisible: 12
        },
		{
            breakpoint: '1024px',
            numVisible: 10
        },
		{
            breakpoint: '768px',
            numVisible: 8
        },
        {
            breakpoint: '560px',
            numVisible: 7
        }
	];
	showThumbnails = true;

	@ViewChild(Galleria) galleria!: Galleria;

	public get activeIndex() {
		return this._activeIndex;
	}
	public set activeIndex(index) {
		this._activeIndex = index;

		this.reinitializeImageZoomer();
	}

	public static get isOpen() {
		return this._isVisible;
	}

	public get isVisible() {
		return ImageViewerComponent._isVisible;
	}
	public set isVisible(isVisible) {
		if (isVisible)
			this.windowResizeSubscription = this.windowResize$.subscribe(() => this.reinitializeImageZoomer());
		else {
			this.keydownUnlisten();
			this.windowResizeSubscription.unsubscribe();
		}

		ImageViewerComponent._isVisible = isVisible;
	}

	public get showItemNavigators() {
		return this.images && this.images.length > 1;
	}

	private _activeIndex!: number;
	private static _isVisible = false;
	private lastIndex!: number;
	private keydownUnlisten!: () => void;
	private windowResize$ = fromEvent(window, 'resize');
	private windowResizeSubscription!: Subscription;

	constructor(
		private imageViewerService: ImageViewerService,
		private renderer: Renderer2,
		private cd: ChangeDetectorRef,
		private http: HttpClient
	) { }
	
	ngOnInit() {
		this.imageViewerService.onShowImageViewer$.subscribe(sourceData => {
			if (sourceData && 'fileAttachmentData' in sourceData && sourceData.fileAttachmentData) {
				this.images = sourceData.fileAttachmentData.filter(fa => fa.isImage).map(fa => ({
					description: fa.description,
					fileName: fa.name,
					src: fa.href,
					thumbnailSrc: `${fa.href}?thumbnail=true`
				}) as GalleriaData);

				this.lastIndex = this.images.length - 1;
				this.activeIndex = this.images.findIndex(img => img.src == sourceData.startingImageHREF);

				const startingImage = this.images[this.activeIndex];

				// Queue the starting image first
				this.setDataUrl(startingImage);

				this.images.forEach(img => {
					if (img != startingImage)
						this.setDataUrl(img)
				});
			}
			else if (sourceData && 'href' in sourceData) {
				this.images = [{
						fileName: '',
						src: sourceData.href,
						thumbnailSrc: sourceData.href,
						dataUrl: sourceData.href
					} as GalleriaData];
				this.activeIndex = 0;
			}
			else
				this.activeIndex = this.images.findIndex(img => img.src == sourceData.startingImageHREF);
			
			this.isVisible = true;

			this.keydownUnlisten = this.renderer.listen('document', 'keydown', this.onKeydown);
		});
	}

	onThumbnailButtonClick() {
		this.showThumbnails = !this.showThumbnails;

		this.reinitializeImageZoomer();
	}

	/** Causes NgxImageZoom to reinstantiate its zoom component. This is necessary in order to reset properties of the
	 * component that have no public api surface, like the current zoom level and ratios used to calculate dimensions. 
	 * Without doing this, things get wonky when switching between images with notably different resolutions.
	 */
	private reinitializeImageZoomer() {
		this.reinitializingNgxImageZoom = true;
		this.cd.detectChanges();
		this.reinitializingNgxImageZoom = false;
	}

	private onKeydown = (e: KeyboardEvent) => {
		if (e.key == 'ArrowRight' && this.showItemNavigators)
			this.activeIndex = this.activeIndex == this.lastIndex ? 0 : this.activeIndex + 1;
		else if (e.key == 'ArrowLeft' && this.showItemNavigators)
			this.activeIndex = this.activeIndex == 0 ? this.lastIndex : this.activeIndex - 1;
		else if (e.key == 'Escape')
			this.galleria.onMaskHide(); // hides galleria and removes keydown listener
	}

	private setDataUrl = (img: GalleriaData) => {
		this.http.get(img.src, {
			responseType: 'blob'
		})
		.subscribe(blob => {
			const reader = new FileReader();

			reader.addEventListener('load', () => {
				img.dataUrl = reader.result as string;
			});

			reader.readAsDataURL(blob);
		});
	}

	private overrideZoomFunction(comp: NgxImageZoomComponent) {
		const originalFunc: Function = comp['zoomOn'].bind(comp);

		function overrideFunc(this: NgxImageZoomComponent,...params: any[]) {
			originalFunc(...params);
			comp.setMagnification = comp['magnification'] = 1.75;
		}

		comp['zoomOn'] = overrideFunc.bind(comp);
	}
}